import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { getCollateral } from "../../lib/collateral";
import { COMPANY, IMAGES } from "../../lib/data";
import { B, CollateralBar } from "../../components/site/CollateralKit";

export default function ProductValueMatrix() {
  const { product } = useParams();
  const c = getCollateral(product);
  if (!c) return <Navigate to="/solutions" replace />;
  const pdf = `/${c.slug}-value-matrix.pdf`;

  return (
    <div className="min-h-screen bg-charcoal/20 print:bg-white">
      <CollateralBar product={c.slug} label="Value Matrix · One-Page" pdf={pdf} testid="matrix" />
      <div className="py-8 print:py-0 flex justify-center">
        <div data-testid="collateral-sheet" className="bg-white shadow-2xl print:shadow-none w-[297mm] h-[210mm] p-[11mm] flex flex-col" style={{ fontSize: "10px" }}>
          <header className="border-b-2 border-ink pb-3">
            <div className="flex items-start justify-between gap-6">
              <div className="max-w-[62%]">
                <div className="flex items-center gap-2 mb-1.5">
                  <img src={IMAGES.markDark} alt="OneHarvest" className="h-6 w-6 object-contain" />
                  <span className="font-display font-extrabold tracking-tight text-ink text-sm">ONEHARVEST</span>
                  <span className="ml-2 border border-flame/40 bg-flame/10 text-flame px-2 py-0.5 text-[8px] uppercase tracking-[0.18em]">{c.name} · Value Proposition Matrix</span>
                </div>
                <h1 className="font-display font-black tracking-tight text-ink leading-[1] text-[29px]">{c.matrixHeadline}</h1>
                <p className="mt-2 text-charcoal leading-snug text-[11px]">{c.matrixSub}</p>
              </div>
              <div className="w-[36%] space-y-1.5">
                {c.pillars.map(([t, d]) => (
                  <div key={t} className="border border-border px-2.5 py-1.5">
                    <span className="font-display font-bold text-ink text-[11px]">{t}</span>
                    <span className="text-muted-foreground text-[9px]"> — {d}</span>
                  </div>
                ))}
              </div>
            </div>
          </header>

          <div className="flex-1 flex flex-col min-h-0 pt-3">
            <div className="grid grid-cols-[1.15fr_1.5fr_1.5fr_1.15fr] bg-ink text-white text-[8px] uppercase tracking-[0.16em]">
              <div className="px-3 py-2">Target Role</div>
              <div className="px-3 py-2 border-l border-white/10">Critical Pain Point</div>
              <div className="px-3 py-2 border-l border-white/10">The {c.name} Solution</div>
              <div className="px-3 py-2 border-l border-white/10 text-flame">Business Outcome</div>
            </div>
            <div className="flex-1 grid grid-rows-5 border-x border-b border-border min-h-0">
              {c.personas.map((p, i) => (
                <div key={p.role} className={`grid grid-cols-[1.15fr_1.5fr_1.5fr_1.15fr] ${i % 2 ? "bg-muted/40" : "bg-white"} border-t border-border first:border-t-0`}>
                  <div className="px-3 py-2 border-l-[3px] border-flame flex flex-col justify-center">
                    <div className="font-display font-bold text-ink text-[12px] leading-tight">{p.role}</div>
                    <div className="text-[8px] uppercase tracking-wider text-muted-foreground mt-0.5">{p.segment}</div>
                  </div>
                  <div className="px-3 py-2 border-l border-border text-charcoal text-[10px] leading-snug flex items-center"><p><B t={p.pain} /></p></div>
                  <div className="px-3 py-2 border-l border-border text-charcoal text-[10px] leading-snug flex flex-col justify-center">
                    <span className="inline-flex self-start items-center bg-flame/10 text-flame border border-flame/30 px-1.5 py-0.5 text-[7.5px] uppercase tracking-wider font-semibold mb-1">{p.tag}</span>
                    <p><B t={p.solution} /></p>
                  </div>
                  <div className="px-3 py-2 border-l border-border flex flex-col justify-center gap-1">
                    {p.outcomes.map((o) => (
                      <div key={o} className="flex items-start gap-1.5"><ArrowRight className="h-3 w-3 text-flame flex-shrink-0 mt-0.5" /><span className="font-display font-bold text-ink text-[10px] leading-tight">{o}</span></div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 bg-ink text-white flex items-center justify-between gap-4 px-5 py-3">
            <div>
              <div className="font-display font-extrabold text-[15px] leading-tight">Match {c.name} to the buyer — not just the spec.</div>
              <div className="text-white/60 text-[10px] mt-0.5">{COMPANY.phone} · {COMPANY.email} · {COMPANY.serviceArea}</div>
            </div>
            <Link to={`/collateral/${c.slug}/calculator`} className="inline-flex items-center gap-1.5 bg-flame text-white px-4 py-2.5 font-semibold text-[11px] hover:bg-flame/90 transition-colors print:hidden flex-shrink-0">Run the ROI Calculator <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
          <p className="mt-2 text-[8px] text-muted-foreground text-center">Representative positioning &amp; figures are illustrative estimates for evaluation only — not a quote, guarantee, or warranty of any specific result, and not financial, engineering, or legal advice. Actual outcomes vary by application. © {new Date().getFullYear()} {COMPANY.legal}.</p>
        </div>
      </div>
    </div>
  );
}
